import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { HiOutlineSearch } from "react-icons/hi";
import { MdClear, MdOutlineVisibility } from "react-icons/md";
import Button from "../Utilities/Button";
import Modal from "../Utilities/Modal";
import PaginationNew from "../Utilities/PaginationNew";
import RightPanelLayout from "../Layout/RightPanelLayout";
import styles from "./style/AiWaveLeadList.module.scss";
import { fetchAiWaveLeadsList } from "./aiWaveLeadApi";

const LIMIT = 20;

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return `${format(parsed, "yyyy-MM-dd")} ${format(parsed, "HH:mm")}`;
};

// modules/pains/services 統一是 { id, name } 陣列；industry 是單一 { id, name }。
// name 可能是 null（前端資料改版後舊資料對不到名字），這種情況退回顯示 id。
const IdNameTags = ({ items, emptyText = "—", limit, className = "" }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <span className={styles.emptyText}>{emptyText}</span>;
  }
  const visible = limit ? items.slice(0, limit) : items;
  const hiddenCount = limit ? items.length - visible.length : 0;
  return (
    <div className={`${styles.tagList} ${className}`}>
      {visible.map((item, index) => (
        <span key={`${item?.id ?? "tag"}-${index}`} className={styles.tag}>
          {item?.name || item?.id || "—"}
        </span>
      ))}
      {hiddenCount > 0 ? (
        <span className={styles.tagMore}>+{hiddenCount}</span>
      ) : null}
    </div>
  );
};

const AiWaveLeadList = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [openedLead, setOpenedLead] = useState(null);

  const applyListSearch = () => {
    setSearchApplied(searchInput.trim());
    setPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError("");
    fetchAiWaveLeadsList({ search: searchApplied, page, limit: LIMIT })
      .then((data) => {
        if (cancelled) {
          return;
        }
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(Number(data?.total) || 0);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        setRows([]);
        setTotal(0);
        setListError(err?.message || "載入失敗");
      })
      .finally(() => {
        if (!cancelled) {
          setListLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [page, searchApplied]);

  const onPageChange = (selectedObj) => {
    setPage(selectedObj.selected + 1);
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / LIMIT)),
    [total],
  );

  return (
    <RightPanelLayout title="AI WAVE 展場名單">
      <div className={styles.listToolbar}>
        <div className={styles.listToolbarTitle}>展場配方卡名單</div>
        <div className={styles.toolbarActions}>
          <div className={styles.searchBar}>
            <button
              type="button"
              className={styles.searchBarIconBtn}
              onClick={applyListSearch}
              aria-label="搜尋"
            >
              <HiOutlineSearch className={styles.searchBarIcon} />
            </button>
            <input
              type="search"
              className={styles.searchBarInput}
              value={searchInput}
              placeholder="搜尋姓名／電話／Email／公司"
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applyListSearch();
                }
              }}
              aria-label="搜尋展場名單"
            />
            {searchInput ? (
              <button
                type="button"
                className={styles.searchBarClear}
                onClick={() => {
                  setSearchInput("");
                  setSearchApplied("");
                  setPage(1);
                }}
                aria-label="清除搜尋"
              >
                <MdClear className={styles.searchBarClearIcon} />
              </button>
            ) : null}
          </div>
          <Button type="primary" title="搜尋" onClick={applyListSearch} />
        </div>
      </div>

      {!listLoading && total > 0 && (
        <div className={styles.tablePaginationTop}>
          <PaginationNew
            onPageChange={onPageChange}
            pageCount={page}
            limit={LIMIT}
            totalPages={totalPages}
            totalData={total}
          />
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>訪客編號</th>
              <th>姓名</th>
              <th>電話</th>
              <th>E-mail</th>
              <th>公司</th>
              <th>產業</th>
              <th>選擇模組</th>
              <th>提交時間</th>
              <th>詳情</th>
            </tr>
          </thead>
          <tbody>
            {listLoading ? (
              <tr>
                <td colSpan={9} className={styles.tableEmptyCell}>
                  載入中…
                </td>
              </tr>
            ) : null}
            {!listLoading && listError ? (
              <tr>
                <td colSpan={9} className={styles.tableEmptyCell}>
                  {listError}
                </td>
              </tr>
            ) : null}
            {!listLoading && !listError && rows.length > 0
              ? rows.map((lead) => (
                  <tr key={lead._id}>
                    <td className={styles.latinCell}>{lead.visitorNo ?? "—"}</td>
                    <td>{lead.name}</td>
                    <td className={styles.latinCell}>{lead.phone}</td>
                    <td className={styles.latinCell}>{lead.email}</td>
                    <td>{lead.company || "—"}</td>
                    <td>
                      <IdNameTags items={lead.industry ? [lead.industry] : []} />
                    </td>
                    <td>
                      <IdNameTags items={lead.modules} limit={3} />
                    </td>
                    <td className={styles.latinCell}>
                      {formatDateTime(lead.createdAt)}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.linkButton}
                        aria-label="查看詳情"
                        onClick={() => setOpenedLead(lead)}
                      >
                        <MdOutlineVisibility />
                      </button>
                    </td>
                  </tr>
                ))
              : null}
            {!listLoading && !listError && rows.length === 0 ? (
              <tr>
                <td colSpan={9} className={styles.tableEmptyCell}>
                  目前沒有符合條件的展場名單。
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={Boolean(openedLead)}
        size="lg"
        width="min(680px, calc(100vw - 32px))"
      >
        <div className={styles.leadModalRoot}>
          <header className={styles.leadModalTop}>
            <h2 className={styles.leadModalTitle}>訪客詳情</h2>
          </header>
          <div className={styles.leadModalBody}>
            {openedLead ? (
              <>
                <div className={styles.leadModalSection}>
                  <div className={styles.leadModalRow}>
                    <span className={styles.leadModalLabel}>訪客編號</span>
                    <span className={styles.latinCell}>
                      {openedLead.visitorNo ?? "—"}
                    </span>
                  </div>
                  <div className={styles.leadModalRow}>
                    <span className={styles.leadModalLabel}>姓名</span>
                    <span>{openedLead.name}</span>
                  </div>
                  <div className={styles.leadModalRow}>
                    <span className={styles.leadModalLabel}>電話</span>
                    <span className={styles.latinCell}>{openedLead.phone}</span>
                  </div>
                  <div className={styles.leadModalRow}>
                    <span className={styles.leadModalLabel}>E-mail</span>
                    <span className={styles.latinCell}>{openedLead.email}</span>
                  </div>
                  <div className={styles.leadModalRow}>
                    <span className={styles.leadModalLabel}>公司</span>
                    <span>{openedLead.company || "—"}</span>
                  </div>
                  <div className={styles.leadModalRow}>
                    <span className={styles.leadModalLabel}>提交時間</span>
                    <span className={styles.latinCell}>
                      {formatDateTime(openedLead.createdAt)}
                    </span>
                  </div>
                </div>
                <div className={styles.leadModalSection}>
                  <div className={styles.leadModalLabel}>產業</div>
                  <IdNameTags
                    items={openedLead.industry ? [openedLead.industry] : []}
                  />
                </div>
                <div className={styles.leadModalSection}>
                  <div className={styles.leadModalLabel}>選擇模組</div>
                  <IdNameTags items={openedLead.modules} />
                </div>
                <div className={styles.leadModalSection}>
                  <div className={styles.leadModalLabel}>經營困境</div>
                  <IdNameTags items={openedLead.pains} />
                </div>
                <div className={styles.leadModalSection}>
                  <div className={styles.leadModalLabel}>推薦微服務</div>
                  <IdNameTags items={openedLead.services} />
                </div>
              </>
            ) : null}
          </div>
          <footer className={styles.leadModalFooter}>
            <button
              type="button"
              className={styles.leadModalCloseBtn}
              onClick={() => setOpenedLead(null)}
            >
              關閉
            </button>
          </footer>
        </div>
      </Modal>
    </RightPanelLayout>
  );
};

export default AiWaveLeadList;
